import {
  Button,
  ConfigProvider,
  Table,
  Tag,
  Tooltip,
  notification,
} from "antd";
import { iff } from "core/iff";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { customizeRenderEmpty } from ".";
import { getDefaultWorkspace } from "app-redux/selectors/licensingAccounts.selector";
import {
  getWorkspaceInvites,
  resendInvite,
  revokeInvite,
} from "services/MemberService";
import { useMediaQuery } from "react-responsive";
import { useSelector } from "react-redux";
import { getCurrentWorkspaceOrgs } from "app-redux/selectors/orgs.selector";

function useColumns() {
  const { t } = useTranslation();
  const [resending, setResending] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const wsOrgs = useSelector(getCurrentWorkspaceOrgs)

  const showNotification = (
    message: string,
    description: string,
    error: boolean = false
  ) => {
    notification[error ? "error" : "success"]({
      message: <b style={{ color: "#14151C" }}>{message}</b>,
      description,
      style: {
        borderRadius: 4,
        borderLeft: `3px solid #${error ? "EB4B4B" : "19A04B"}`,
      },
    });
  };

  const resend = (invitationId: string) => {
    setResending(invitationId);
    resendInvite(invitationId)
      .then(() => {
        showNotification(
          t("member.Invite successfully resend"),
          t("member.The invite for this user has been resend") + "."
        );
      })
      .catch(() => {
        showNotification(
          t("member.Resend invitation failed"),
          t("member.The invitation failed") +
            ". " +
            t("member.Try again") +
            ".",
          true
        );
      })
      .finally(() => setResending(null));
  };

  const revoke = (invitationId: string) => {
    setRevoking(invitationId);
    revokeInvite(invitationId)
      .then(() => {
        showNotification(
          t("member.Invite successfully revoked"),
          t("member.The invite for this user has been revoked") + "."
        );
      })
      .catch(() => {
        showNotification(
          t("member.Revoke invitation failed"),
          t("member.The invitation revoke failed") +
            ". " +
            t("member.Try again") +
            ".",
          true
        );
      })
      .finally(() => setRevoking(null));
  };
  const isMobile = useMediaQuery({ query: "(max-width: 719px)" });

  return [
    {
      title: t("global.label.mail"),
      sorter: (a: any, b: any) =>
        a.email_address.localeCompare(b.email_address),
      dataIndex: "email_address",
      key: "email_address",
    },
    {
      title: t("Organization"),
      dataIndex: "org_id",
      key: "org_id",
      render: (org_id: string) => <span>{wsOrgs.find(org => org.id === org_id)?.name ?? org_id}</span>
    },
    {
      title: t("member.title.role"),
      dataIndex: "rbac_roles",
      key: "rbac_roles",
      render: (rbac_roles: any) => {
        if (rbac_roles && rbac_roles.length >= 1) {
          return isMobile ? (
            <span>
              {rbac_roles?.map((role: any) => t(role.role_id))?.join(", ")}
            </span>
          ) : (
            <Tooltip
              title={
                <span>
                  {rbac_roles?.map((role: any) => t(role.role_id))?.join(", ")}
                </span>
              }
            >
              <span>
                {iff(
                  rbac_roles.length > 1,
                  <div style={{ display: "flex" }}>
                    {`${rbac_roles[0].role_id},`}
                    <div className="memberRolesBiggerThanOne">
                      {rbac_roles.length - 1}+
                    </div>
                  </div>,
                  <>{t(rbac_roles[0].role_id)}</>
                )}
              </span>
            </Tooltip>
          );
        }
      },
    },
    {
      title: t("member.title.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color="#FFE4AE" style={{ color: "#744A0B", margin: 0, border: 0 }}>
          {status.toLocaleUpperCase()}
        </Tag>
      ),
    },
    {
      title: t("member.title.actions"),
      key: "actions",
      dataIndex: "actions",
      fixed: "right" as const,
      width: 200,
      render: (_text: string, record: any) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            type="text"
            onClick={() => resend(record.id)}
            loading={resending === record.id}
          >
            {t("member.Resend")}
          </Button>
          <div style={{ margin: "0 4px" }}>•</div>
          <Button
            type="text"
            onClick={() => revoke(record.id)}
            loading={revoking === record.id}
          >
            {t("member.Revoke")}
          </Button>
        </div>
      ),
    },
  ];
}

interface Invitation {
  name: string;
  email_address: string;
  accept_status: string;
  id: string;
  rbac_roles: null | Array<{
    role_id: string;
  }>;
  org_id?: string;
}

type InviteStatusResponse = Array<{
  invite_status: Invitation[];
  organization_id: string;
}>;

export function PendingMembers({ selectedOrgs }: { selectedOrgs: string[] }) {
  const isMobile = useMediaQuery({ query: "(max-width: 719px)" });
  const { t } = useTranslation();
  const pendingColumns = useColumns();

  const currentWorkspaceId = getDefaultWorkspace();

  const [loading, setLoading] = useState<boolean>(true);
  const [filteredInvitations, setFilteredInvitations] = useState<Invitation[]>(
    []
  );
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  useEffect(() => {
    setLoading(true);
    if (currentWorkspaceId) {
      getWorkspaceInvites(currentWorkspaceId)
        .then((response: InviteStatusResponse) => {
          const invitations = response
            .map((currOrgInvites: InviteStatusResponse[number]) => {
              return (
                currOrgInvites.invite_status
                  .filter(
                    (invitation) => invitation.accept_status === "PENDING"
                  )
                  // set org on invitation
                  .map((invitation) => {
                    invitation.org_id = currOrgInvites.organization_id;
                    return invitation;
                  })
              );
            })
            .flat();
          setInvitations(invitations);
          setFilteredInvitations(invitations);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    setFilteredInvitations(
      invitations.filter(inv => selectedOrgs.length === 0 || selectedOrgs.includes(inv.org_id ?? ''))
    );
  }, [selectedOrgs, invitations]);

  return (
    <ConfigProvider
      renderEmpty={() =>
        customizeRenderEmpty({
          t,
          title: "member.No pending invitations found",
          subtitle: "",
        })
      }
    >
      <Table
        scroll={{ x: isMobile ? "max-content" : undefined }}
        rowKey={(record) => record.id + record.org_id}
        columns={pendingColumns}
        dataSource={filteredInvitations}
        loading={loading}
        pagination={false}
      />
    </ConfigProvider>
  );
}
